// Monta os grupos da sidebar de GESTÃO consumindo a MESMA fonte da navbar antiga
// (`buildRoutes` de @constants/navigation-routes). Garante módulos, grupos, ordem
// e condições de visibilidade IDÊNTICOS à nav antiga — aqui só adaptamos o shape
// para o design da nav 2026 e aplicamos as tratativas do novo contexto:
//   - FP entra na gestão apontando para a RAIZ `fp` (como o goToFP da nav antiga);
//   - Comissões/Tesouraria resolvem o destino real (mesmos helpers da nav antiga);
//   - Market News / Suporte abrem janela externa (window.open), como na nav antiga;
//   - `new: true` vira badge "Novo"; `noAccessIcon` mantém o estado de upsell.
import { additionalPermissions } from '@utils/AdditionalPermissions'
import { ROTA_IMERSIVA } from '@modules/aawz-intelligence-v2/views/chat-imersivo/constants'
import {
  buildRoutes,
  comissoesRouteName,
  treasuryRouteName,
  financeSettingsRouteName,
} from '@constants/navigation-routes'

// Itens externos (abrem nova janela) — VERBATIM da nav antiga (openMarketWindow / openSuporte).
const ROUTE_MARKET_WINDOW = 'openMarketWindow'
const ROUTE_SUPORTE = 'suporte'
const SUPORTE_URL = 'https://aawz.zendesk.com/hc/pt-br'
const MARKET_PROD_PREFIX = '/investimentos/'
const MARKET_PATH = 'mercado'
// Destinos dinâmicos (mesmo `name` da nav antiga; o destino real é resolvido por helper).
const ROUTE_COMISSOES = 'comissoes'
const ROUTE_TREASURY = 'treasury'
// Financial Planning: na gestão o item é SEMPRE visível (can_show:true, upsell via
// noAccessIcon), mas `fp.home` exige a flag FINANCIAL_PLANNING_SIMULATOR e devolveria
// 404 a quem não a tem. O destino é a raiz `fp`, cujo redirect resolve home/PGBL/
// welcome pela matriz Cross-Sell × Flags — mesmo comportamento do goToFP da nav antiga.
const ROUTE_FP_HOME = 'fp.home'
const ROUTE_FP_ROOT = 'fp'
// Subrota dinâmica: Configurações > Financeiro vai à Controladoria V2 quando o
// usuário tem acesso, senão às configurações financeiras antigas — mesma regra do
// card Financeiro de settings/view-home-v2 (helper financeSettingsRouteName).
const ROUTE_FINANCE_SETTINGS = 'settings-v2.finance'
// Marca um item standalone (fora de grupo) na saída da nav antiga.
const GROUP_FALSE = false
// Entre os standalone, a Home da sidebar é UMA: a "Home IA" (`ROTA_IMERSIVA`),
// quando a home por IA está liberada, ocupa o lugar da Home comum — é a home do
// escritório (o guard da raiz já manda `/` para `/home-ia`); senão, a Home.
// `buildProfissionalRoutes` já emite só uma das duas; se as duas chegarem, a
// Home IA vence e a comum SOME — nunca as duas empilhadas (decisão do usuário,
// review #2254). Os demais standalone viram itens de topo.
const ROUTE_HOME = 'home'
const ROUTE_HOME_IA = ROTA_IMERSIVA
const BADGE_NOVO = 'Novo'
// AAWZ Hub: a rota `solicitacoes` sai do grupo em que a nav antiga a coloca e vira
// item avulso do topo da sidebar, renomeado. O clique navega DIRETO para o módulo —
// a sub-navegação continua nas abas in-page dele, e não na sidebar.
const AAWZ_HUB_ROUTE = 'solicitacoes'
const AAWZ_HUB_LABEL = 'AAWZ Hub'

// URL da janela externa de Market News (depende do ambiente, como na nav antiga).
function marketNewsUrl() {
  const prefix = process.env.NODE_ENV === 'production' ? MARKET_PROD_PREFIX : ''
  return `${prefix}${MARKET_PATH}`
}

function externalUrl(routeName) {
  if (routeName === ROUTE_MARKET_WINDOW) return marketNewsUrl()
  if (routeName === ROUTE_SUPORTE) return SUPORTE_URL
  return null
}

// Destino real do item. Consolidador já vem resolvido no `name` pelo buildRoutes;
// Comissões/Tesouraria resolvem pelos mesmos helpers da nav antiga.
function resolveRouteName(route, access, currentUser) {
  if (route.name === ROUTE_FP_HOME) return ROUTE_FP_ROOT
  if (route.name === ROUTE_COMISSOES) return comissoesRouteName(access)
  if (route.name === ROUTE_TREASURY) {
    return treasuryRouteName(currentUser, access) || route.name
  }
  return route.name
}

// Destino real de uma subrota. A maioria cai direto no próprio `name`; a única
// dinâmica hoje é Configurações > Financeiro (Controladoria V2 vs. config antiga).
function resolveSubRouteName(subRoute, access, currentUser) {
  if (subRoute.name === ROUTE_FINANCE_SETTINGS) {
    return financeSettingsRouteName(currentUser, access)
  }
  return subRoute.name
}

// Subitem da nav 2026 a partir de uma subRoute da nav antiga. As subRoutes já vêm
// FILTRADAS por permissão/flags pelas funções *OptionsRoutes (ex.: financeOptionsRoutes
// aplica filterRoutes internamente), então aqui só normalizamos o shape e resolvemos
// o destino dinâmico quando aplicável.
function toSubitem(subRoute, access, currentUser) {
  return {
    key: subRoute.name,
    label: (subRoute.title || '').trim(),
    routeName: resolveSubRouteName(subRoute, access, currentUser),
  }
}

function toItem(route, access, currentUser) {
  const item = {
    key: route.name,
    label: (route.title || '').trim(),
    routeName: resolveRouteName(route, access, currentUser),
    icon: route.icon,
  }
  if (route.new) item.badge = BADGE_NOVO
  if (route.noAccessIcon) item.noAccessIcon = true
  const external = externalUrl(route.name)
  if (external) item.external = external
  if (route.subRoutes && route.subRoutes.length) {
    item.subitems = route.subRoutes
      .filter(Boolean)
      .map((sub) => toSubitem(sub, access, currentUser))
  }
  return item
}

// Item do topo a partir da rota da nav antiga. Reusa toItem para herdar ícone,
// noAccessIcon e badge sem duplicar a regra de montagem — só o rótulo muda.
function toTopItem(route, access, currentUser) {
  const item = toItem(route, access, currentUser)
  item.label = AAWZ_HUB_LABEL
  return item
}

// Item Home (standalone, renderizado primeiro). VERBATIM da nav antiga.
function toHome(entry) {
  return {
    key: entry.name,
    label: entry.title,
    routeName: entry.name,
    icon: entry.icon,
  }
}

// Itens de um grupo da nav antiga, com a rota do AAWZ Hub DESVIADA para fora do
// grupo: ela é devolvida à parte, para virar item avulso do topo. Grupo que fica
// sem itens depois do desvio é descartado por quem chama.
function collectGroupItems(entry, access, currentUser) {
  const items = []
  let hubRoute = null
  ;(entry.routes || []).forEach((route) => {
    if (!route) return
    if (route.name === AAWZ_HUB_ROUTE) {
      hubRoute = route
      return
    }
    items.push(toItem(route, access, currentUser))
  })
  return { items, hubRoute }
}

// O que ocupa o slot da Home: a Home IA sempre vence; a Home comum só entra se
// o slot ainda está vazio (a ordem em que as duas chegam não muda o resultado).
function escolherHome(atual, entry) {
  if (entry.name === ROUTE_HOME_IA) return toHome(entry)
  return atual || toHome(entry)
}

/**
 * Mapeia a saída do buildRoutes (grupos/rotas da nav antiga) para o shape da nav
 * 2026 ({ home, topItems, groups }). Função PURA — sem store, fácil de testar.
 * Aplica as tratativas (badge "Novo", noAccessIcon, externos, dinâmicos) e desvia
 * o AAWZ Hub dos grupos para o bloco de topo — onde também vão os standalone
 * (`group:false`) que não ocupam o slot da Home, na ordem da nav antiga.
 *
 * @param {Array} built  saída de buildRoutes (já filtrada por can_show/only_manager)
 * @param {Object} access store.getters['parameters/show']
 * @param {Object} currentUser this.$currentUser()
 * @returns {{ home: Object|null, topItems: Array, groups: Array<{title:string, items:Array}> }}
 */
export function adaptGestaoGroups(built, access, currentUser) {
  let home = null
  let hubRoute = null
  const topItems = []
  const groups = []

  ;(built || []).forEach((entry) => {
    if (!entry) return
    if (entry.group === GROUP_FALSE) {
      if (entry.name === ROUTE_HOME || entry.name === ROUTE_HOME_IA) {
        home = escolherHome(home, entry)
      } else {
        topItems.push(toItem(entry, access, currentUser))
      }
      return
    }
    const collected = collectGroupItems(entry, access, currentUser)
    if (collected.hubRoute) hubRoute = collected.hubRoute
    if (!collected.items.length) return
    groups.push({
      title: entry.hiddenTitle ? '' : entry.title,
      items: collected.items,
    })
  })

  if (hubRoute) topItems.push(toTopItem(hubRoute, access, currentUser))
  return { home, topItems, groups }
}

/**
 * Monta { home, topItems, groups } da sidebar de gestão a partir do buildRoutes da
 * nav antiga. `buildRoutes` já entrega a lista filtrada por can_show / only_manager
 * / validShow, na ordem e nos grupos da navbar antiga — então a paridade é
 * automática, inclusive o gating do AAWZ Hub por feature flag e por IS_INTERNAL.
 *
 * @param {Object} ctxBase { access, currentUser, hideCrossSell, showWorkspace }
 * @returns {{ home: Object|null, topItems: Array, groups: Array<{title:string, items:Array}> }}
 */
export function buildGestaoGroups(ctxBase) {
  const access = (ctxBase && ctxBase.access) || {}
  const currentUser = (ctxBase && ctxBase.currentUser) || {}
  const ctx = {
    access,
    currentUser,
    hideCrossSell: ctxBase && ctxBase.hideCrossSell,
    showWorkspace: ctxBase && ctxBase.showWorkspace,
    professionalsOnlyManager: additionalPermissions.professionals(),
    // A nav 2026 ignora subrotas por enquanto; o destino é sempre o routeName.
    getSubRoutesBI: () => [],
    // Navegação no clique é por routeName/external; handlers não são usados aqui.
    handlers: {},
  }

  return adaptGestaoGroups(buildRoutes(ctx) || [], access, currentUser)
}
